'use client';

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { Form, FormControl, FormField, FormItem } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
// import { useToast } from '@/components/ui/use-toast';
import { cn } from '@/lib/utils';
import style from './RequestForm.module.scss';
import Button from '@/components/ui/Button/Button';
import InputMask from 'react-input-mask';
import { sendForm } from '@/app/api/forms/sendForm';
import toast from 'react-hot-toast';

const formSchema = z.object({
  full_name: z.string().min(3),
  phone: z.string().min(3),
  email: z.string().email(),
  message: z.string().min(3),
});

type FormSchema = z.infer<typeof formSchema>;

const defaultValues = {
  full_name: '',
  phone: '',
  email: '',
  message: '',
};

export const RequestForm = () => {
  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
    defaultValues,
  });
  // const { toast } = useToast();

  const onSubmit = async (data: FormSchema) => {
    try {
      await sendForm(data, { form_type: 'Get in touch (industries)' });

      form.reset(defaultValues);

      toast.success('The form successfully submitted');
    } catch (error) {
      console.error(error);
      toast.error('Something went wrong');
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className={style.form}>
        <div className="grid grid-cols-[168px_1fr]">
          <p className="text-left text-2xl font-semibold leading-[46px]">
            Get in Touch
          </p>
          <p className="text-left text-sm font-normal leading-[26px]">
            Need this service? Let’s talk about how Tytum can meet and exceed
            your expectations.
          </p>
        </div>
        <FormField
          control={form.control}
          name="full_name"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input
                  placeholder={'Type your name'}
                  className={cn(
                    'md:w-96',
                    form.formState.errors.full_name && 'border-destructive',
                    'bg-white'
                  )}
                  {...field}
                />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="phone"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <InputMask mask="(999) 999-9999" {...field}>
                  <Input
                    placeholder={'Type your phone'}
                    className={cn(
                      'md:w-96',
                      form.formState.errors.phone && 'border-destructive',
                      'bg-white',
                      style.input
                    )}
                  />
                </InputMask>
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input
                  placeholder={'Type your email'}
                  type="email"
                  className={cn(
                    'md:w-96',
                    form.formState.errors.email && 'border-destructive',
                    'bg-white'
                  )}
                  {...field}
                />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="message"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Textarea
                  placeholder={'Type your message'}
                  style={{ minHeight: '60px' }}
                  className={cn(
                    'md:w-96',
                    form.formState.errors.message && 'border-destructive',
                    'bg-white'
                  )}
                  {...field}
                />
              </FormControl>
            </FormItem>
          )}
        />
        <div className="pt-1 text-left">
          <Button
            variant="main"
            shape="square"
            type="submit"
            disabled={form?.formState?.isSubmitSuccessful}
            style={{ width: '100%' }}
          >
            <p className="text-center text-[18px]">
              {form?.formState?.isSubmitSuccessful
                ? 'Form Submitted'
                : 'Send Message'}
            </p>
          </Button>
        </div>
      </form>
    </Form>
  );
};
